import {
  Box, Button, ButtonBase, IconButton, Link as MuiLink, Popover, Skeleton, Stack, Tooltip, Typography, useTheme,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { endEdge } from './menuOrigin';
import { notificationTarget } from '@/features/notifications/target';
import { notificationsApi } from '@/api/resources';
import { qk } from '@/api/queryKeys';
import { formatRelative } from '@/lib/format';
import { paths } from '@/routes/paths';
import { EmptyState } from '@/components/common/EmptyState';

/**
 * The bell and its popover. Unread is an aqua dot — the signal colour, on the
 * bell and beside each unread row — with a heavier title; there is no count
 * badge on the bar. The number is in the button's accessible name.
 */
export function NotificationsMenu() {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const unread = useQuery({
    queryKey: qk.notificationsUnread,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
  });

  const list = useQuery({
    queryKey: qk.list('notifications', { size: 8 }),
    queryFn: () => notificationsApi.list({ page: 0, size: 8 }),
    enabled: Boolean(anchor),
  });

  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.root('notifications') }),
  });

  const markOne = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.root('notifications') }),
  });

  const items = list.data?.content ?? [];
  const unreadCount = unread.data ?? 0;
  const label = unreadCount
    ? `${t('notification.title')} — ${unreadCount} ${t('notification.unread')}`
    : t('notification.title');

  return (
    <>
      <Tooltip title={t('notification.title')}>
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          sx={{
            position: 'relative',
            width: 38,
            height: 38,
            color: 'text.secondary',
            bgcolor: anchor ? 'brand.selected' : 'transparent',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          }}
        >
          <NotificationsNoneRoundedIcon sx={{ fontSize: 21 }} />
          {unreadCount ? (
            <Box
              aria-hidden
              data-testid="unread-dot"
              sx={{
                position: 'absolute',
                top: 7,
                insetInlineEnd: 8,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'brand.signal',
                boxShadow: (th) => `0 0 0 2px ${th.palette.background.paper}`,
              }}
            />
          ) : null}
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: endEdge(theme.direction) }}
        transformOrigin={{ vertical: 'top', horizontal: endEdge(theme.direction) }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': t('notification.title'),
            sx: { width: 380, maxWidth: '92vw', mt: 0.75, border: 1, borderColor: 'divider', overflow: 'hidden' },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.75, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{t('notification.title')}</Typography>
          {unreadCount ? (
            <MuiLink
              component="button"
              type="button"
              onClick={() => markAll.mutate()}
              underline="hover"
              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
            >
              {t('notification.markAllRead')}
            </MuiLink>
          ) : null}
        </Stack>
        {/* Loading is not empty: "no notifications" while the first page is
            still on its way was a false statement for a second. */}
        {list.isPending ? (
          <Stack aria-busy="true" aria-label={t('common.loading')} sx={{ px: 1.75, py: 1.5, gap: 1.75 }}>
            {[0, 1, 2].map((row) => (
              <Stack key={row} spacing={0.5} sx={{ paddingInlineStart: 2.25 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="30%" />
              </Stack>
            ))}
          </Stack>
        ) : list.isError ? (
          <Stack alignItems="center" spacing={1} sx={{ px: 1.75, py: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{t('errors.loadFailed')}</Typography>
            <Button size="small" variant="outlined" color="inherit" onClick={() => void list.refetch()}>
              {t('common.retry')}
            </Button>
          </Stack>
        ) : items.length === 0 ? (
          <EmptyState dense title={t('notification.empty')} />
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 400, overflowY: 'auto' }}>
            {items.map((item) => (
              <Box component="li" key={item.id} sx={{ '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}>
                <ButtonBase
                  onClick={() => {
                    if (item.id && !item.read) markOne.mutate(item.id);
                    const target = notificationTarget(item);
                    if (target) {
                      setAnchor(null);
                      navigate(target);
                    }
                  }}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: 1.25,
                    px: 1.75,
                    py: 1.5,
                    textAlign: 'start',
                    fontFamily: 'inherit',
                    '&:hover': { bgcolor: 'brand.tableHead' },
                    '&.Mui-focusVisible': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box
                    sx={{
                      flex: '0 0 8px',
                      height: 8,
                      mt: '7px',
                      borderRadius: '50%',
                      bgcolor: item.read ? 'transparent' : 'brand.signal',
                    }}
                  >
                    {item.read ? null : (
                      <Box component="span" sx={visuallyHidden}>
                        {t('notification.unread')}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: item.read ? 400 : 600,
                        color: item.read ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      {item.message ?? t(`notification.${item.type}`, { defaultValue: item.type ?? '' })}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                      {formatRelative(item.createdAt)}
                    </Typography>
                  </Box>
                </ButtonBase>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.75, py: 1.25 }}>
          <MuiLink
            component={Link}
            to={paths.notifications}
            onClick={() => setAnchor(null)}
            underline="hover"
            sx={{ fontSize: '0.8125rem', fontWeight: 600 }}
          >
            {t('common.view')}
          </MuiLink>
        </Box>
      </Popover>
    </>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
